import { v } from 'h3-valibot';
import type {
  FloatingButtonIconPosition,
  FloatingButtonIconSize,
  FloatingButtonPosition,
  FloatingButtonSize,
  FloatingButtonStyleType
} from '@hypercodestudio/basler-components';

import { parseURL } from '~/utils/validation/parseURL';

export const FLOATING_BUTTON_POSITIONS = [
  'right-center',
  'left-center',
  'bottom-right',
  'bottom-left'
] as const satisfies readonly FloatingButtonPosition[];

export const FLOATING_BUTTON_SIZES = [
  'small',
  'medium',
  'large'
] as const satisfies readonly FloatingButtonSize[];

export const FLOATING_BUTTON_STYLE_TYPES = [
  'primary',
  'secondary',
  'attraction',
  'attraction-secondary',
  'remarkable'
] as const satisfies readonly FloatingButtonStyleType[];

export const FLOATING_BUTTON_ICON_POSITIONS = [
  'top',
  'left',
  'right'
] as const satisfies readonly FloatingButtonIconPosition[];

export const FLOATING_BUTTON_ICON_SIZES = [
  'small',
  'medium',
  'large',
  'dynamic'
] as const satisfies readonly FloatingButtonIconSize[];

export const FLOATING_CONTACT_SEGMENT_IDS = [
  'contact-form',
  'contact-options'
] as const;

export type FloatingContactSegmentId =
  (typeof FLOATING_CONTACT_SEGMENT_IDS)[number];

const contactFormSegmentSchema = v.object({
  id: v.literal('contact-form'),
  title: v.optional(v.string()),
  iconName: v.optional(v.string()),
  pardotUrl: parseURL
});

const contactOptionsSegmentSchema = v.object({
  id: v.literal('contact-options'),
  title: v.optional(v.string()),
  iconName: v.optional(v.string())
});

export const segmentConfigSchema = v.union([
  contactFormSegmentSchema,
  contactOptionsSegmentSchema
]);

const offsetConfigSchema = v.union([
  v.string(),
  v.object({
    inline: v.optional(v.string()),
    block: v.optional(v.string())
  })
]);

const panelConfigSchema = v.object({
  segments: v.optional(v.pipe(v.array(segmentConfigSchema), v.minLength(1)))
});

export const buttonConfigSchema = v.object({
  id: v.pipe(v.string(), v.minLength(1)),
  label: v.pipe(v.string(), v.minLength(1)),
  iconName: v.optional(v.string()),
  size: v.optional(v.picklist(FLOATING_BUTTON_SIZES)),
  styleType: v.optional(v.picklist(FLOATING_BUTTON_STYLE_TYPES)),
  href: v.optional(v.string()),
  target: v.optional(v.string()),
  rel: v.optional(v.string()),
  position: v.optional(v.picklist(FLOATING_BUTTON_POSITIONS)),
  usePdpActionBarOffset: v.optional(v.boolean()),
  offset: v.optional(offsetConfigSchema),
  panel: v.optional(panelConfigSchema)
});

export const floatingButtonLayoutSchema = v.object({
  position: v.optional(v.picklist(FLOATING_BUTTON_POSITIONS)),
  gap: v.optional(v.string()),
  animationDelay: v.optional(v.pipe(v.number(), v.minValue(0))),
  iconPosition: v.optional(v.picklist(FLOATING_BUTTON_ICON_POSITIONS)),
  iconSize: v.optional(v.picklist(FLOATING_BUTTON_ICON_SIZES)),
  usePdpActionBarOffset: v.optional(v.boolean()),
  offset: v.optional(offsetConfigSchema)
});

export const floatingContactButtonConfigurationSchema = v.object({
  floatingButton: v.optional(floatingButtonLayoutSchema),
  buttons: v.pipe(v.array(buttonConfigSchema), v.minLength(1), v.maxLength(3))
});

export type ParsedContactFormSegment = {
  id: 'contact-form';
  title?: string;
  iconName?: string;
  pardotUrl: string;
};

export type ParsedContactOptionsSegment = {
  id: 'contact-options';
  title?: string;
  iconName?: string;
};

export type ParsedFloatingContactSegment =
  ParsedContactFormSegment | ParsedContactOptionsSegment;

export type ParsedFloatingContactButton = {
  id: string;
  label: string;
  iconName?: string;
  size?: FloatingButtonSize;
  styleType?: FloatingButtonStyleType;
  href?: string;
  target?: string;
  rel?: string;
  position?: FloatingButtonPosition;
  usePdpActionBarOffset?: boolean;
  offset?: string | { inline?: string; block?: string };
  panel?: {
    segments?: ParsedFloatingContactSegment[];
  };
};

export type ParsedFloatingButtonLayout = {
  position?: FloatingButtonPosition;
  gap?: string;
  animationDelay?: number;
  iconPosition?: FloatingButtonIconPosition;
  iconSize?: FloatingButtonIconSize;
  usePdpActionBarOffset?: boolean;
  offset?: string | { inline?: string; block?: string };
};

export type ParsedFloatingContactConfiguration = {
  floatingButton: ParsedFloatingButtonLayout;
  buttons: ParsedFloatingContactButton[];
};
